import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  EmptyState,
  Pagination,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner, PageHeader } from '@shared/ui';
import { useOrdersQuery, type OrderStatus } from '@entities/order';
import { OrdersTable } from '@features/order-list';

const STATUSES: OrderStatus[] = [
  'NEW',
  'IN_PROCESSING',
  'READY_TO_SHIP',
  'IN_TRANSIT',
  'AT_CUSTOMS',
  'DELIVERED',
  'CANCELLED',
];

export function OrdersListPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const status = (params.get('status') as OrderStatus | null) ?? undefined;
  const page = Number(params.get('page') ?? 0);

  const query = useMemo(
    () => ({
      status: status ? [status] : undefined,
      page,
      size: 20,
    }),
    [status, page],
  );

  const { data, isLoading, isError, error, refetch } = useOrdersQuery(query);

  const updateParam = (k: string, v: string | null) => {
    const merged = new URLSearchParams(params);
    if (v === null) merged.delete(k);
    else merged.set(k, v);
    setParams(merged, { replace: true });
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={t('orders.title')} subtitle={t('orders.subtitle')} />

      <Select
        value={status ?? 'ALL'}
        onValueChange={(v) => updateParam('status', v === 'ALL' ? null : v)}
      >
        <SelectTrigger className="w-56">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="ALL">{t('orders.filter.allStatuses')}</SelectItem>
          {STATUSES.map((s) => (
            <SelectItem key={s} value={s}>
              {t(`orders.status.${s}`)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : isError ? (
        <p>
          {t('products.loadFail', { message: error?.message ?? 'unknown' })}{' '}
          <button type="button" className="underline" onClick={() => void refetch()}>
            {t('common.retry')}
          </button>
        </p>
      ) : data && data.items.length === 0 ? (
        <EmptyState
          title={t('orders.empty.title')}
          description={t('orders.empty.description')}
        />
      ) : data ? (
        <>
          <OrdersTable data={data.items} />
          <Pagination
            page={data.page}
            totalPages={data.totalPages}
            onPageChange={(p) => updateParam('page', String(p))}
          />
        </>
      ) : null}
    </div>
  );
}
